import * as React from 'react';
import {
  Modal,
  ModalOverlay,
  Dialog,
  Button as RACButton,
} from 'react-aria-components';
import { MenuLine, CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import { useMediaQuery } from '../hooks/use-media-query';

/**
 * Klaret AppShell (handoff §4.3).
 * Topbar 56 px; --tvl-canvas at 92% opacity + backdrop-filter: blur(8px); hairline bottom.
 * Sidebar 264 px expanded / 64 px rail (icons only) collapsed.
 * Mobile: sidebar becomes a drawer (RAC Modal/Dialog — no Radix Dialog).
 * No lucide, no Radix.
 */

const RAIL_WIDTH = 64;           // §4.3 collapsed rail
const RAIL_EXPANDED_WIDTH = 264; // §4.3 expanded sidebar
const SUBTAB_WIDTH = 220;
const HOVER_CLOSE_DELAY_MS = 280;

export interface AppShellProps extends React.HTMLAttributes<HTMLDivElement> {
  topbar?: React.ReactNode;
  /** Primary navigation rail. Always shown as 64px collapsed; smoothly expands to 264px overlay on hover. */
  sidebar?: React.ReactNode;
  /** Secondary navigation column for the current section. Always visible (when provided), 220px wide. */
  subtab?: React.ReactNode;
  /**
   * Navigation for the mobile drawer. When given, the drawer renders THIS
   * instead of `sidebar` + `subtab`.
   *
   * The desktop rail (icons) and the desktop subtab column are two separate
   * surfaces because there is room for both. Stacking those same two components
   * into the drawer produced a flat section list with a disconnected sub-page
   * list underneath it — so the app also grew a bottom tab bar and an on-page
   * chip row to compensate, leaving THREE competing navigations on a phone.
   * One accordion panel replaces all of it.
   */
  mobileNav?: React.ReactNode;
  bottomTabs?: React.ReactNode;
  banner?: React.ReactNode;
  children?: React.ReactNode;
  mainClassName?: string;
  /**
   * Ref to the scrolling `<main>`. Exposed so an app can attach behaviours that need the real
   * scroll container rather than a wrapper inside it — the edge-swipe-back gesture (which must be
   * reachable across the full viewport, including below short content) and pull-to-refresh (which
   * must read this element's `scrollTop`).
   */
  mainRef?: React.Ref<HTMLElement>;
}

interface AppShellContextValue {
  /** Whether the rail is currently expanded (hover overlay). */
  railExpanded: boolean;
  openMobileDrawer: () => void;
  closeMobileDrawer: () => void;
  isMobile: boolean;
  hasSubtab: boolean;
}

const AppShellContext = React.createContext<AppShellContextValue | null>(null);

export function useAppShell(): AppShellContextValue {
  const ctx = React.useContext(AppShellContext);
  if (!ctx) throw new Error('useAppShell must be used within <AppShell>');
  return ctx;
}

export const AppShell = React.forwardRef<HTMLDivElement, AppShellProps>(
  (
    { className, mainClassName, topbar, sidebar, subtab, mobileNav, bottomTabs, banner, children, mainRef, ...rest },
    ref,
  ) => {
    const isMobile = !useMediaQuery('(min-width: 768px)');

    const [railExpanded, setRailExpanded] = React.useState(false);
    const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const onRailEnter = React.useCallback(() => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
      setRailExpanded(true);
    }, []);

    const onRailLeave = React.useCallback(() => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => setRailExpanded(false), HOVER_CLOSE_DELAY_MS);
    }, []);

    React.useEffect(
      () => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
      },
      [],
    );

    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const openMobileDrawer = React.useCallback(() => setDrawerOpen(true), []);
    const closeMobileDrawer = React.useCallback(() => setDrawerOpen(false), []);

    const ctxValue = React.useMemo<AppShellContextValue>(
      () => ({
        railExpanded,
        openMobileDrawer,
        closeMobileDrawer,
        isMobile,
        hasSubtab: Boolean(subtab),
      }),
      [railExpanded, openMobileDrawer, closeMobileDrawer, isMobile, subtab],
    );

    return (
      <AppShellContext.Provider value={ctxValue}>
        <div
          ref={ref}
          className={cn(
            // dvh, NOT vh. Tailwind's `screen` height scale resolves to `100vh`, and iOS
            // resolves vh against the LARGEST viewport (toolbar hidden). With the Safari
            // toolbar showing, the shell is therefore taller than the screen and the page
            // ends in a band of dead canvas you can scroll into. globals.css already sizes
            // <body> in 100dvh for exactly this reason, and the rail below already uses
            // `calc(100dvh - var(--tvl-topbar-total))`.
            //
            // Deliberately worded WITHOUT the literal utility name: Tailwind's scanner reads
            // comments too, so spelling it out here regenerates the very `100vh` rule this
            // file removes. That dead rule is harmless in itself, but it makes the artifact
            // grep for `min-height:100vh` report a leftover that no element uses — which is
            // exactly the false signal the sweep was verifying against.
            //
            // Invisible in the Home Screen app (no toolbar ⇒ vh == dvh); this is the
            // browser-tab case.
            'flex min-h-[100dvh] w-full flex-col bg-[var(--tvl-canvas)] text-[var(--tvl-ink)]',
            className,
          )}
          {...rest}
        >
          {/* Banner + topbar are ONE sticky block that carries the status-bar inset ONCE.
           *
           * THE BUG THIS FIXES: the inset used to be chosen with `banner ? … : …`, but `banner` is
           * a JSX element (`<ServiceStatusBanner />`) and a JSX element is ALWAYS truthy — even
           * though that component returns null whenever there is no incident, which is nearly
           * always. So the shell rendered an empty, NON-STICKY spacer holding the inset, and gave
           * the header the no-inset branch on the assumption the banner had taken it. At scroll 0
           * that looked correct; scrolled, the spacer moved away and the sticky header pinned to
           * the viewport top with no inset — directly under the iOS clock. A parent cannot ask
           * whether a child rendered anything, so the condition could never have been right.
           *
           * Making the pair one sticky container removes the question: the inset is on the element
           * that actually pins, applies whether or not the banner draws anything, and cannot be
           * double-counted. The blur and hairline move here too so the whole pinned area is one
           * surface. The header keeps its own 56px, so `--tvl-topbar-total` (56px + inset) still
           * describes the block's height for the rail offsets.
           */}
          {(topbar || banner) ? (
            <div
              className={cn(
                'sticky top-0 z-[var(--tvl-z-topbar)] w-full',
                'pt-[var(--tvl-safe-top)]',
                'border-b border-[var(--tvl-hairline)]',
                'bg-[var(--tvl-canvas)]/[0.92] backdrop-blur-[8px]',
                // opaque fallback when backdrop-filter unsupported
                'supports-[not(backdrop-filter:blur(0))]:bg-[var(--tvl-canvas)]',
              )}
            >
              {banner}
              {topbar ? (
                <header className="flex h-14 w-full items-center">{topbar}</header>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-1 min-h-0 w-full">
            {/* Desktop rail — sticky wrapper so it never scrolls with main content */}
            {sidebar && !isMobile ? (
              <div
                className="sticky top-[var(--tvl-topbar-total)] self-start shrink-0"
                style={{
                  width: RAIL_WIDTH,
                  // dvh + the status-bar inset, matching the sticky offset above; with plain
                  // vh the rail runs past the bottom of an iPhone screen.
                  height: 'calc(100dvh - var(--tvl-topbar-total))',
                  zIndex: railExpanded ? 60 : 30,
                }}
                onMouseEnter={onRailEnter}
                onMouseLeave={onRailLeave}
                // Keyboard parity: expand when focus enters a rail item (onFocus/onBlur
                // bubble from the focused descendant) so the labels are reachable without
                // a mouse. The close-delay timer absorbs focus moving between items.
                onFocus={onRailEnter}
                onBlur={onRailLeave}
              >
                <aside
                  className={cn(
                    'absolute top-0 left-0 h-full flex flex-col',
                    'border-r border-[var(--tvl-hairline)] bg-[var(--tvl-canvas)]',
                    'overflow-hidden',
                    'transition-[width,box-shadow] duration-500',
                    'will-change-[width]',
                    railExpanded
                      ? 'z-[var(--tvl-z-drawer-nav)] shadow-[var(--tvl-e2)]'
                      : 'z-[var(--tvl-z-sticky)]',
                  )}
                  style={{
                    width: railExpanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH,
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                  data-rail-state={railExpanded ? 'expanded' : 'collapsed'}
                  role="navigation"
                  aria-label="Primary navigation"
                  aria-expanded={railExpanded}
                >
                  {sidebar}
                </aside>
              </div>
            ) : null}

            {/* Subtab — always visible on desktop */}
            {subtab && !isMobile ? (
              <aside
                className={cn(
                  'sticky top-[var(--tvl-topbar-total)] hidden md:flex shrink-0 flex-col',
                  'h-[calc(100dvh-var(--tvl-topbar-total))]',
                  'border-r border-[var(--tvl-hairline)] bg-[var(--tvl-canvas)]',
                  'overflow-y-auto',
                )}
                style={{ width: SUBTAB_WIDTH, zIndex: 20 }}
                role="navigation"
                aria-label="Section navigation"
              >
                {subtab}
              </aside>
            ) : null}

            {/* Mobile drawer — RAC Modal/Dialog replaces Radix Dialog */}
            {(mobileNav || sidebar || subtab) && isMobile ? (
              <ModalOverlay
                isOpen={drawerOpen}
                onOpenChange={setDrawerOpen}
                isDismissable
                className={cn(
                  'fixed inset-0 z-[var(--tvl-z-drawer-nav)]',
                  'bg-[var(--tvl-scrim)]',
                  'entering:animate-in entering:fade-in-0',
                  'exiting:animate-out exiting:fade-out-0',
                )}
              >
                <Modal
                  className={cn(
                    'fixed inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col',
                    'bg-[var(--tvl-canvas)] border-r border-[var(--tvl-hairline)]',
                    'shadow-[var(--tvl-e3)]',
                    'entering:animate-in entering:slide-in-from-left',
                    'exiting:animate-out exiting:slide-out-to-left',
                  )}
                >
                  <Dialog
                    aria-label="Navigation"
                    className="flex h-full flex-col outline-none"
                  >
                    {/* Drawer header */}
                    <div className="flex h-14 items-center justify-between border-b border-[var(--tvl-hairline)] px-3">
                      <span className="text-[14px] font-[500] leading-[20px] text-[var(--tvl-ink)]">
                        Menu
                      </span>
                      <RACButton
                        onPress={() => setDrawerOpen(false)}
                        aria-label="Close navigation"
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-[var(--tvl-r2)]',
                          'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)]',
                          'outline-none',
                          'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
                        )}
                      >
                        <CloseLine size={16} color="currentColor" aria-hidden />
                      </RACButton>
                    </div>

                    {/* One panel. `mobileNav` (an accordion) wins; the
                        sidebar+subtab stacking is the legacy fallback for
                        consumers that have not supplied one. */}
                    {mobileNav ? (
                      <div className="flex-1 overflow-y-auto">{mobileNav}</div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-y-auto">{sidebar}</div>
                        {subtab ? (
                          <div className="border-t border-[var(--tvl-hairline)]">{subtab}</div>
                        ) : null}
                      </>
                    )}
                  </Dialog>
                </Modal>
              </ModalOverlay>
            ) : null}

            {/* Main content */}
            <main
              ref={mainRef}
              className={cn(
                // `relative` so an app can anchor an overlay to the scroll container itself (the
                // pull-to-refresh indicator). Deliberately not `fixed` in the consumer: the
                // swipe-back gesture transforms this element, and a transformed ancestor makes
                // `fixed` descendants resolve against IT rather than the viewport.
                'relative flex-1 min-w-0 overflow-y-auto',
                // A COLUMN, not a block. As a block, <main> gave its children an auto-height
                // containing block, so a page asking for `h-full` got `height: 100%` of `auto`
                // — which resolves to auto, i.e. nothing. Pages written to fill the frame
                // (the workspace Overview says so in its own header comment) therefore sized
                // to their content and left the lower two-thirds of a large monitor empty
                // while looking, in source, as though they filled it. Column layout gives the
                // page a definite height to resolve against.
                //
                // Safe for every other page: a block-level child of a column flex container
                // still stretches across and still sizes to its content (flex-grow defaults
                // to 0), so nothing that did not ask to grow changes.
                'flex flex-col',
                'px-4 py-4 md:px-6 md:py-6',
                // Clear the fixed bottom bar AND the home indicator beneath it.
                // A flat `pb-16` matched the bar's nominal height only, so on a
                // notched phone the last row of content sat under the bar.
                isMobile && bottomTabs
                  ? 'pb-[calc(5rem+env(safe-area-inset-bottom))]'
                  : '',
                mainClassName,
              )}
            >
              {children}
            </main>
          </div>

          {/* Mobile bottom tabs */}
          {isMobile && bottomTabs ? (
            <div
              className={cn(
                'fixed inset-x-0 bottom-0 z-[var(--tvl-z-sticky)] md:hidden',
                'border-t border-[var(--tvl-hairline)] bg-[var(--tvl-canvas)]',
              )}
            >
              {bottomTabs}
            </div>
          ) : null}
        </div>
      </AppShellContext.Provider>
    );
  },
);
AppShell.displayName = 'AppShell';

export interface AppShellMobileMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

export const AppShellMobileMenuButton = React.forwardRef<
  HTMLButtonElement,
  AppShellMobileMenuButtonProps
>(({ className, onClick, ...props }, ref) => {
  const { openMobileDrawer, isMobile } = useAppShell();
  if (!isMobile) return null;
  return (
    <button
      ref={ref}
      type="button"
      onClick={(e) => {
        openMobileDrawer();
        onClick?.(e);
      }}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-[var(--tvl-r2)]',
        'text-[var(--tvl-ink)] hover:bg-[var(--tvl-wash-hover)] md:hidden',
        'outline-none',
        'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
        className,
      )}
      aria-label="Open navigation"
      {...props}
    >
      <MenuLine size={20} color="currentColor" aria-hidden />
    </button>
  );
});
AppShellMobileMenuButton.displayName = 'AppShellMobileMenuButton';
